import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  OnInit,
  ChangeDetectionStrategy,
} from '@angular/core';
import { LoggerService } from '../../../../../services/logger.service';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ColorPickerModule } from '@iplab/ngx-color-picker';
import { SectionAccordionComponent } from '../../../../../shared/components/section-accordion';
import { TextEditorComponent, TextEditorStyle } from '../../../../../shared/components/text-editor';
import { InfoLayout1PreviewComponent, InfoLayout1Data } from './layouts/layout_1';
import { InfoLayout2PreviewComponent, InfoLayout2Data } from './layouts/layout_2';
import { InfoLayout3PreviewComponent, InfoLayout3Data } from './layouts/layout_3';
import { InfoLayout4PreviewComponent, InfoLayout4Data } from './layouts/layout_4';
import { InfoLayout5PreviewComponent, InfoLayout5Data } from './layouts/layout_5';
import { TemplateService } from '../../../../../car-wash-website/services/template.service';
import { NotificationService } from '../../../../../services/notification.service'; // NotificationService;

export interface InfoImage {
  src?: string;
  alt?: string;
  width?: string;
  height?: string;
  scaletype?: 'fill' | 'fit' | 'stretch' | 'cover' | 'contain';
}

export interface InfoPanelData {
  title: string;
  description: string;
  titleStyle?: TextEditorStyle;
  subtitle?: string; // Large title for layout_5 (e.g., "THE GRIME FIGHTERS")
  imageUrl?: string;
  image?: InfoImage;
  imageFit: 'fill' | 'fit' | 'stretch';
  buttonText: string;
  buttonNavigation: 'homepage' | 'express-washes' | 'express-detail' | 'fleets' | 'coupons';
  backgroundColor: string;
  layout?:
    'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5' | '1' | '2' | '3' | '4' | '5'; // Info section layout: layout_1=image left/text right, layout_2=text left/image right, layout_3=title left/description right/image bottom, layout_4=description left/title right/image bottom, layout_5=grime fighters layout
}

type TabType = 'content' | 'layout';
type SectionType = 'heading' | 'media' | 'button' | 'background';

/**
 * MinimalInfoPanelComponent provides a configurable info/marketing panel editor for a website builder.
 * It exposes inputs to seed initial content and styling, emits change/save/close events, and includes
 * UI helpers for editing text, images, layouts and background colors (including a color picker and image upload).
 * Designed to produce data structures for multiple preview layouts (layout_1 .. layout_5) and to keep a small set
 * of custom/predefined colors for quick selection.
 * @component
 * @selector app-minimal-info-panel
 * @example
 *   <app-minimal-info-panel
 *     [data]="panelData"
 *     [headingColor]="#000000"
 *     (valueChange)="onPanelChange($event)"
 *     (save)="onPanelSave($event)"
 *     (close)="onCloseEditor()">
 *   </app-minimal-info-panel>
 * @input {InfoPanelData} data - Initial panel data (title, description, imageUrl, imageFit, buttonText, buttonNavigation, backgroundColor, layout). Defaults to an internal DEFAULT_DATA if not provided.
 * @input {string | null} headingColor - Optional heading text color override.
 * @input {string | null} paragraphColor - Optional paragraph/body text color override.
 * @input {string | null} primaryColor - Optional primary color override for controls/buttons.
 * @input {string | null} headingFont - Optional font family for heading text.
 * @input {string | null} bodyFont - Optional font family for body text.
 * @output {EventEmitter<void>} close - Emitted when the user requests to close the editor/panel.
 * @output {EventEmitter<InfoPanelData>} save - Emitted when the user saves the panel; payload is the current InfoPanelData.
 * @output {EventEmitter<InfoPanelData>} valueChange - Emitted whenever the internal data changes (live updates).
 *
 * Key lifecycle hooks:
 * - ngOnInit(): Loads any custom color from input data on initialization.
 * - ngOnChanges(changes: SimpleChanges): Detects changes to input data and reloads custom color if backgroundColor changed.
 *
 * Important public methods:
 * - onClose(): Emit close event to parent.
 * - onSave(): Emit save event with current data.
 * - onTabChange(tab: TabType): Switch active editor tab (e.g., content, style).
 * - onTitleChange(value: string): Update title and emit valueChange.
 * - onTitleStyleChange(style: TextEditorStyle): Update title style and emit valueChange.
 * - onDescriptionChange(value: string): Update description and emit valueChange.
 * - onReplaceImage(): Trigger file input to replace the image.
 * - onImageFitChange(event: Event): Update imageFit (scaletype) from select and emit valueChange.
 * - onButtonTextChange(value: string): Update button text and emit valueChange.
 * - onButtonNavigationChange(value: string): Update navigation target and emit valueChange.
 * - onBackgroundColorChange(color: string): Apply background color and emit valueChange.
 * - onLayoutChange(layoutNumber: '1'|'2'|'3'|'4'|'5'): Convert and set layout to API format (layout_#) and emit valueChange.
 * - getSelectedLayout(): Normalize and return the selected layout as '1'..'5' (handles both legacy and layout_# formats).
 *
 * Data transformation / preview helpers:
 * - getScaletypeValue(): Internal helper to resolve proper image scaletype ('fill'|'fit'|'stretch'|'cover'|'contain').
 * - getInfoLayout1Data() ... getInfoLayout5Data(): Build and return layout-specific data objects for previews/components.
 * - getInfoBackgroundColor(): Returns resolved background color (falls back to '#FFFFFF').
 *
 * Color picker and custom color management:
 * - onAddColor(): Shortcut to open the color picker.
 * - openColorPicker(color?): Open the color picker modal/preview; sets currentPickerColor.
 * - closeColorPicker(): Close picker, normalize color to hex, apply as background and add as a custom color (max 1).
 * - onColorPickerChange(): Live-preview color changes (does not persist to custom list until closed).
 * - addCustomColor(color: string): Private helper to add a non-predefined custom color (replaces existing).
 *
 * Section and UI helpers:
 * - toggleSection(section: SectionType): Expand/collapse the given editor section.
 * - isSectionExpanded(section: SectionType): Return whether a section is currently expanded.
 *
 * Image upload flow and validation:
 * - createFileInput(): Creates a hidden file input and triggers file selection.
 * - handleFileUpload(file: File): Handles temporary preview, calls TemplateService.uploadWebsiteFile(file),
 *   replaces temporary URL with uploaded URL on success, sets isUploadingImage and uploadError appropriately,
 *   and emits valueChange with updated data.
 * - validateFileSize(file: File): Ensures file is under 5MB (alerts and returns false if too large).
 *
 * Other notes:
 * - Maintains PREDEFINED_COLORS and allows a single custom color to be stored for quick reuse.
 * - Uses TemplateService to upload files and attempts to extract uploaded file URL from several common response shapes.
 */
@Component({
  selector: 'app-minimal-info-panel',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ColorPickerModule,
    SectionAccordionComponent,
    TextEditorComponent,
    InfoLayout1PreviewComponent,
    InfoLayout2PreviewComponent,
    InfoLayout3PreviewComponent,
    InfoLayout4PreviewComponent,
    InfoLayout5PreviewComponent,
  ],
  templateUrl: './info-panel.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './info-panel.component.scss',
})
export class MinimalInfoPanelComponent implements OnInit, OnChanges {
  @Input() data: InfoPanelData = this.getDefaultData();
  @Input() headingColor: string | null = null;
  @Input() paragraphColor: string | null = null;
  @Input() primaryColor: string | null = null;
  @Input() headingFont: string | null = null;
  @Input() bodyFont: string | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() save = new EventEmitter<InfoPanelData>();
  @Output() valueChange = new EventEmitter<InfoPanelData>();

  activeTab: TabType = 'content';
  expandedSection: SectionType | null = 'heading';
  customColors: string[] = [];

  // Color picker state (same as dark theme)
  isColorPickerOpen = false;
  currentPickerColor: string | any = '#000000';

  // Image upload state
  isUploadingImage = false;
  uploadError: string | null = null;

  private static readonly DEFAULT_DATA: InfoPanelData = {
    title: '',
    description: '',
    imageUrl: '',
    imageFit: 'fill',
    buttonText: '',
    buttonNavigation: 'express-washes',
    backgroundColor: '#FFFFFF',
    layout: '1', // Default to layout 1 (image left, text right)
  };

  private static readonly PREDEFINED_COLORS = ['#5E19A1', '#FF9736', '#079271', '#1B5BFF'];

  constructor(
    private logger: LoggerService,
    private templateService: TemplateService,
    private messageService: NotificationService
  ) {}

  // Lifecycle hook - called on component initialization
  ngOnInit(): void {
    // Load custom color on initial load
    this.loadCustomColorFromData();
  }

  // Lifecycle hook to detect when data input changes
  ngOnChanges(changes: SimpleChanges): void {
    if (changes['data'] && this.data?.backgroundColor) {
      // Load custom color if backgroundColor is not in predefined colors
      this.loadCustomColorFromData();
    }
  }

  // Load custom color from data if it's not a predefined color
  /**
   * Loads a custom background color from the component's data and updates the component's customColors array accordingly.
   * @example
   * loadCustomColorFromData()
   * // void — updates this.customColors based on this.data.backgroundColor
   * @returns {void} Updates internal state; does not return a value.
   **/
  private loadCustomColorFromData(): void {
    if (!this.data?.backgroundColor) {
      return;
    }

    const bgColor = this.data.backgroundColor;

    // Check if the color is not in predefined colors
    if (!MinimalInfoPanelComponent.PREDEFINED_COLORS.includes(bgColor)) {
      // Add to custom colors (maximum 1 custom color)
      this.customColors = [bgColor];
    } else {
      // If it's a predefined color, clear custom colors
      this.customColors = [];
    }
  }

  // Public methods
  onClose(): void {
    this.close.emit();
  }

  onSave(): void {
    this.save.emit(this.data);
  }

  onTabChange(tab: TabType): void {
    this.activeTab = tab;
  }

  onTitleChange(value: string): void {
    this.data.title = value;
    this.valueChange.emit(this.data);
  }

  onTitleStyleChange(style: TextEditorStyle): void {
    this.data.titleStyle = style;
    this.valueChange.emit(this.data);
  }

  onDescriptionChange(value: string): void {
    this.data.description = value;
    this.valueChange.emit(this.data);
  }

  onReplaceImage(): void {
    this.createFileInput();
  }

  onImageFitChange(event: Event): void {
    const target = event.target as HTMLSelectElement;
    this.data.imageFit = target.value as InfoPanelData['imageFit'];
    this.valueChange.emit(this.data);
  }

  onButtonTextChange(value: string): void {
    this.data.buttonText = value;
    this.valueChange.emit(this.data);
  }

  onButtonNavigationChange(value: string): void {
    this.data.buttonNavigation = value as InfoPanelData['buttonNavigation'];
    this.valueChange.emit(this.data);
  }

  onBackgroundColorChange(color: string): void {
    this.data.backgroundColor = color;
    this.valueChange.emit(this.data);
  }

  onLayoutChange(layoutNumber: '1' | '2' | '3' | '4' | '5'): void {
    // Convert to layout_X format for API structure
    this.data.layout = `layout_${layoutNumber}` as
      'layout_1' | 'layout_2' | 'layout_3' | 'layout_4' | 'layout_5';
    this.valueChange.emit(this.data);
  }

  /**
   * Return the selected layout identifier ('1' through '5') from this.data.layout, handling both "layout_X" and legacy numeric formats; defaults to '1'.
   * @example
   * etSelectedLayout()
   * '1'
   * @returns {{'1'|'2'|'3'|'4'|'5'}} The selected layout identifier as a string ('1'..'5').
   **/
  getSelectedLayout(): '1' | '2' | '3' | '4' | '5' {
    const layout = this.data.layout;
    if (!layout) return '1';

    // Handle layout_X format
    if (typeof layout === 'string' && layout.startsWith('layout_')) {
      const layoutNum = layout.replace('layout_', '');
      if (
        layoutNum === '1' ||
        layoutNum === '2' ||
        layoutNum === '3' ||
        layoutNum === '4' ||
        layoutNum === '5'
      ) {
        return layoutNum as '1' | '2' | '3' | '4' | '5';
      }
    }

    // Handle old format (just number)
    if (typeof layout === 'string' && /^[1-5]$/.test(layout)) {
      return layout as '1' | '2' | '3' | '4' | '5';
    }

    return '1';
  }

  // Helper method to get properly typed scaletype
  private getScaletypeValue(): 'fill' | 'fit' | 'stretch' | 'cover' | 'contain' {
    const value = this.data.image?.scaletype || this.data.imageFit || 'fit';
    // Type guard to ensure proper typing
    if (value === 'cover' || value === 'contain') {
      return value;
    }
    return value as 'fill' | 'fit' | 'stretch';
  }

  /**
   * Builds and returns an InfoLayout1Data object populated from the component's data.
   * @example
   * etInfoLayout1Data()
   * {
   *   title: 'Example Title',
   *   description: 'Example description',
   *   buttonText: 'Click me',
   *   buttonLink: '/example-path',
   *   image: {
   *     src: 'assets/example.png',
   *     alt: 'Example image',
   *     scaletype: 'cover'
   *   },
   *   imageUrl: 'https://example.com/example.png',
   *   backgroundColor: '#ffffff',
   *   titleStyle: { fontSize: '20px', fontWeight: '700' }
   * }
   * @returns {InfoLayout1Data} InfoLayout1Data populated from the component's data.
   */
  getInfoLayout1Data(): InfoLayout1Data {
    return {
      title: this.data.title,
      description: this.data.description,
      buttonText: this.data.buttonText,
      buttonLink: this.data.buttonNavigation,
      image: {
        ...this.data.image,
        scaletype: this.getScaletypeValue(),
      },
      imageUrl: this.data.imageUrl,
      backgroundColor: this.data.backgroundColor,
      titleStyle: this.data.titleStyle,
    };
  }

  /**
   * Return InfoLayout2Data constructed from the component's internal data and computed image scaletype.
   * @example
   * etInfoLayout2Data()
   * {
   *   title: "Example Title",
   *   description: "Example description",
   *   buttonText: "Learn more",
   *   buttonLink: "/learn-more",
   *   image: { src: "image.jpg", alt: "An image", scaletype: "cover" },
   *   imageUrl: "https://example.com/image.jpg",
   *   backgroundColor: "#ffffff",
   *   titleStyle: { fontSize: "24px", fontWeight: "700" }
   * }
   * @returns {{InfoLayout2Data}} InfoLayout2Data assembled from this.data with computed scaletype.
   **/
  getInfoLayout2Data(): InfoLayout2Data {
    return {
      title: this.data.title,
      description: this.data.description,
      buttonText: this.data.buttonText,
      buttonLink: this.data.buttonNavigation,
      image: {
        ...this.data.image,
        scaletype: this.getScaletypeValue(),
      },
      imageUrl: this.data.imageUrl,
      backgroundColor: this.data.backgroundColor,
      titleStyle: this.data.titleStyle,
    };
  }

  getInfoBackgroundColor(): string {
    return this.data.backgroundColor || '#FFFFFF';
  }

  /**
   * Build and return an InfoLayout3Data object assembled from the component's data and the computed scaletype.
   * @example
   * etInfoLayout3Data()
   * {
   *   title: 'Example Title',
   *   description: 'Example description',
   *   buttonText: 'Learn more',
   *   buttonLink: '/learn-more',
   *   image: { src: 'image.png', alt: 'An image', scaletype: 'cover' },
   *   imageUrl: 'https://example.com/image.png',
   *   backgroundColor: '#ffffff',
   *   titleStyle: { fontSize: '24px', fontWeight: '700' }
   * }
   * @returns {InfoLayout3Data} The assembled InfoLayout3Data object constructed from the component's current data and scaletype.
   */
  getInfoLayout3Data(): InfoLayout3Data {
    return {
      title: this.data.title,
      description: this.data.description,
      buttonText: this.data.buttonText,
      buttonLink: this.data.buttonNavigation,
      image: {
        ...this.data.image,
        scaletype: this.getScaletypeValue(),
      },
      imageUrl: this.data.imageUrl,
      backgroundColor: this.data.backgroundColor,
      titleStyle: this.data.titleStyle,
    };
  }

  /**
   * Build and return the data object for the "Info Layout 4" template using the component's current data and computed scaletype.
   * @example
   * etInfoLayout4Data()
   * {
   *   title: 'Sample Title',
   *   description: 'A short description for the info panel.',
   *   buttonText: 'Learn more',
   *   buttonLink: '/learn-more',
   *   image: { src: 'images/sample.jpg', alt: 'Sample image', scaletype: 'cover' },
   *   imageUrl: 'https://example.com/images/sample.jpg',
   *   backgroundColor: '#ffffff',
   *   titleStyle: { fontSize: '20px', fontWeight: '600' }
   * }
   * @returns {{InfoLayout4Data}} The assembled InfoLayout4Data object populated from the component's data and the computed scaletype.
   **/
  getInfoLayout4Data(): InfoLayout4Data {
    return {
      title: this.data.title,
      description: this.data.description,
      buttonText: this.data.buttonText,
      buttonLink: this.data.buttonNavigation,
      image: {
        ...this.data.image,
        scaletype: this.getScaletypeValue(),
      },
      imageUrl: this.data.imageUrl,
      backgroundColor: this.data.backgroundColor,
      titleStyle: this.data.titleStyle,
    };
  }

  /**
   * Create and return an InfoLayout5Data object built from the component's current data.
   * @example
   * etInfoLayout5Data()
   * {
   *   title: "Section Title",
   *   description: "A short description of the section.",
   *   buttonText: "Learn more",
   *   buttonLink: "/learn-more",
   *   image: { src: "https://...", alt: "alt text", width: 800, height: 600, scaletype: "cover" },
   *   imageUrl: "https://...",
   *   backgroundColor: "#ffffff",
   *   titleStyle: { fontSize: "24px", fontWeight: "700" }
   * }
   * @returns {{InfoLayout5Data}} Returns an InfoLayout5Data object populated from the component data.
   **/
  getInfoLayout5Data(): InfoLayout5Data {
    const scaletype = this.getScaletypeValue();
    const image: InfoLayout5Data['image'] = this.data.image
      ? {
          src: this.data.image.src,
          alt: this.data.image.alt,
          width: this.data.image.width,
          height: this.data.image.height,
          scaletype: scaletype,
        }
      : {
          scaletype: scaletype,
        };

    return {
      title: this.data.title,
      description: this.data.description,
      buttonText: this.data.buttonText,
      buttonLink: this.data.buttonNavigation,
      image: image,
      imageUrl: this.data.imageUrl,
      backgroundColor: this.data.backgroundColor,
      titleStyle: this.data.titleStyle,
    };
  }

  onAddColor(): void {
    // Open color picker modal (same as dark theme)
    this.openColorPicker();
  }

  // Open color picker for custom color
  openColorPicker(color?: string): void {
    this.currentPickerColor = color || this.data.backgroundColor || '#000000';
    this.isColorPickerOpen = true;
  }

  // Close color picker and add the selected color to custom colors list
  /**
   * Close the color picker, apply the currently selected color to the background, and store it as a custom color.
   * @example
   * loseColorPicker()
   * undefined
   * @returns {void} No return value.
   **/
  closeColorPicker(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Apply the color immediately
      this.onBackgroundColorChange(hexColor);

      // Add to custom colors (maximum 1 custom color - replace if exists)
      this.addCustomColor(hexColor);
    }
    this.isColorPickerOpen = false;
  }

  // Handle color change from color picker (update preview only, don't add to list yet)
  /**
   * Normalize and apply the currently selected color from the picker to the component state.
   * @example
   * nColorPickerChange()
   * undefined
   * @param {{void}} {{}} - No function arguments; uses component property this.currentPickerColor.
   * @returns {{void}} Updates this.currentPickerColor to a hex string (prefixing '#' if needed) and calls this.onBackgroundColorChange, returns nothing.
   **/
  onColorPickerChange(): void {
    if (this.currentPickerColor) {
      // Extract hex color from color object (chrome-picker returns an object with hex property)
      let hexColor: string;
      if (typeof this.currentPickerColor === 'string') {
        hexColor = this.currentPickerColor;
      } else if (
        this.currentPickerColor &&
        typeof this.currentPickerColor === 'object' &&
        'hex' in this.currentPickerColor
      ) {
        hexColor = (this.currentPickerColor as any).hex;
      } else {
        hexColor = String(this.currentPickerColor);
      }

      // Ensure hex color starts with #
      if (!hexColor.startsWith('#')) {
        hexColor = '#' + hexColor;
      }

      // Update currentPickerColor to the hex string for consistency
      this.currentPickerColor = hexColor;

      // Apply the color immediately for preview (but don't add to custom colors list yet)
      this.onBackgroundColorChange(hexColor);
    }
  }

  toggleSection(section: SectionType): void {
    const wasExpanded = this.expandedSection === section;

    if (wasExpanded) {
      // If clicking an expanded section, just collapse it
      this.expandedSection = null;
    } else {
      // If clicking a collapsed section, expand this one
      this.expandedSection = section;
    }
  }

  isSectionExpanded(section: SectionType): boolean {
    return this.expandedSection === section;
  }

  // Private helper methods
  private getDefaultData(): InfoPanelData {
    return { ...MinimalInfoPanelComponent.DEFAULT_DATA };
  }

  /**
   * Open a file picker for PNG/JPEG/MP4 files and, if a file is selected and its size is valid, pass it to the upload handler.
   * @example
   * this.createFileInput()
   * undefined
   * @param {void} none - No parameters.
   * @returns {void} No return value.
   **/
  private createFileInput(): void {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.png,.jpg,.jpeg,.mp4';
    input.onchange = (event: Event) => {
      const target = event.target as HTMLInputElement;
      const file = target.files?.[0];
      if (file && this.validateFileSize(file)) {
        this.handleFileUpload(file);
      }
    };
    input.click();
  }

  /**
   * Upload a file with a temporary preview, emit intermediate value changes, and replace the preview with the uploaded image URL or set an error.
   * @example
   * handleFileUpload(file)
   * undefined
   * @param {File} file - The image file to preview locally and upload to the server.
   * @returns {void} No return value; updates component state, emits valueChange events, and handles upload status/errors.
   */
  private handleFileUpload(file: File): void {
    // Show loading state
    this.isUploadingImage = true;
    this.uploadError = null;

    // Create temporary preview URL for immediate feedback
    const tempPreviewUrl = URL.createObjectURL(file);
    this.data.imageUrl = tempPreviewUrl;
    if (!this.data.image) {
      this.data.image = {
        src: tempPreviewUrl,
        alt: file.name,
        width: '420px',
        height: '320px',
        scaletype: 'fit',
      };
    } else {
      this.data.image.src = tempPreviewUrl;
    }
    this.valueChange.emit(this.data);

    // Upload to server
    this.templateService.uploadWebsiteFile(file).subscribe({
      next: response => {
        // Extract image URL from response
        let imageUrl = '';
        if (typeof response === 'string') {
          imageUrl = response;
        } else if (response?.url) {
          imageUrl = response.url;
        } else if (response?.data?.url) {
          imageUrl = response.data.url;
        } else if (response?.imageUrl) {
          imageUrl = response.imageUrl;
        } else if (response?.fileUrl) {
          imageUrl = response.fileUrl;
        }

        if (imageUrl) {
          // Replace temporary URL with uploaded URL
          URL.revokeObjectURL(tempPreviewUrl);
          this.data.imageUrl = imageUrl;
          if (this.data.image) {
            this.data.image.src = imageUrl;
          }
          this.valueChange.emit(this.data);
        } else {
          this.logger.error('No image URL in response:', response);
          this.uploadError = 'Failed to get image URL from server';
        }

        this.isUploadingImage = false;
      },
      error: error => {
        this.logger.error('Error uploading image:', error);
        this.uploadError = 'Failed to upload image. Please try again.';
        this.isUploadingImage = false;
        // Keep the temporary preview on error
      },
    });
  }

  private validateFileSize(file: File): boolean {
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      this.messageService.add({
        severity: 'warn',
        summary: 'File Too Large',
        detail: 'File size must be less than 5MB',
      });
      return false;
    }
    return true;
  }

  private addCustomColor(color: string): void {
    // Maximum 1 custom color - replace existing if any
    if (!MinimalInfoPanelComponent.PREDEFINED_COLORS.includes(color)) {
      // Clear existing custom colors and add the new one
      this.customColors = [color];
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
